import { API, graphqlOperation, Storage } from 'aws-amplify';
import hash from 'object-hash';
import { cleanAndSplit } from './NLP';
import { RUN_METADATA_DUMP, getUserData } from './API';
import { setPrefetchedMetadataAIFeature } from './aiService';
import { findFilenames } from './API';
import { ipaToAmirabet } from './amirabet';
import * as Sentry from '@sentry/browser';
import { logSentryException } from './util';
import { logSessionInfo } from './logger';
import { graphQLRequestIAM } from './graphQLService';

const META_VERSION = '1.1';
const SRSurl = process.env.STUDENT_RECORD_STORE_URL;

export async function getWordMetadata(inWord)
{
  try {
    inWord = inWord.toUpperCase();
    const wordLocale = getUserData().locale === 'es-mx' ? 'es_MX' : '';
    const wordForQuery = wordLocale ? `${inWord}|${wordLocale}`: inWord;

    const wordQuery = `query WordQuery($word: String!) {
      word(WORD: $word) {
        items {
          WORD
          PHON
          NPHON
          NSYL
          NLET
          CONC
          COGNATE {
            spanish
          }
          SIGHT
          AREA_AOA
          AREA_discrim
          IMG_URL
          VIDEO_URL
          RHYME
          GRAPHEMIC_BREAKDOWN
          DEFINITION{
            default
          }
          DEFINITION_VIDEO
          FUNFACTS_IMG_URL
          FUNFACTS_DESCRIPTION
          PHONEME_VIDEO_URL
          RIDDLE
          RIDDLE_ANSWER
          MORPHEME
          MORPHEME_MEANING
          MORPHEME_POSITION
          MORPHEME_ALTS
          NAME_MEANING
          NAME_PERSON
          NAME_NATIONALITY
          NAME_FLAG
          log_ngram_counts
          InterventionSelectionMetadata {
            pk
            locale
            cvc
            ERSS_default_ranked
            fallback_default
            high_default
            high_default_ranked
            low_default
            low_default_ranked
            wer
          }
          GSF {
            categories {
              description
              id
            }
            words {
              category
              word
            }
          }
          SYLLABLE_ENCODING
          ALPHSYL
        }
      }
    }`;
    // Note: cache will invalidate if wordQuery changes. Cache will not invalidate
    // if backend changes
    console.log('Getting word metadata for:', inWord, "wordForQuery:", wordForQuery);
    const key = hash(wordQuery + inWord + META_VERSION);
    let rawMetadata = sessionStorage.getItem(key);
    let wordMetadata;
    if(!rawMetadata || hasTimedOut(rawMetadata, key)){
      wordMetadata = await fetchAndStoreWord(wordQuery, wordForQuery, inWord, key);
      console.log('Fetched word metadata for:', inWord, "wordMetadata:", JSON.stringify(wordMetadata));
    } else {
      wordMetadata = JSON.parse(rawMetadata).content;
      console.log('Word metadata in cache for:', inWord, "wordMetadata:", JSON.stringify(wordMetadata));
    }
    // TODO consider doing some validation on wordMetadata
    return(wordMetadata);
  }
  catch(err)
  {
    console.log("ERROR IN GETWORDMETADATA",err);
    logSentryException(new Error('Word metadata fetch failure'), 'Error in getWordMetadata', { originalError: err?.message, originalStack: err?.stack, word: inWord });
    return ({WORD: inWord});
  }
}

const inWordToQueryWord = (inWord, locale) => locale ? `${inWord}|${locale}`: inWord;
const queryWordToInWord = (queryWord, locale) => locale ? queryWord.replace(`|${locale}`) : queryWord.replace("|en_US", "");

/**
 * Gets metadata for all words in param: words, getting those from cache that 
 *   haven't expired, or fetching those in batch that aren't in cache
 * @param {*} words: list of words to get metadata for
 * @param {*} retryCount: number of retries already attempted
 * @returns: list of word metadata in the same order as the param: words
 */
export async function getWordMetadataList(words, retryCount = 0)
{
  const MAX_RETRIES = 2; // 3 tries in total, 2 retries here
  
  try {
    words = words.map(inWord => inWord.toUpperCase().trim());
    const wordLocale = getUserData().locale === 'es-mx' ? 'es_MX' : '';

    const wordsQuery = `query WordsQuery($words: [String!]!) {
      words(WORDS: $words) {
        WORD
        PHON
        NPHON
        NSYL
        NLET
        CONC
        COGNATE {
          spanish
        }
        SENSITIVITY_TAGS
        SIGHT
        AREA_AOA
        AREA_discrim
        IMG_URL
        VIDEO_URL
        RHYME
        GRAPHEMIC_BREAKDOWN
        DEFINITION{
          default
        }
        DEFINITION_VIDEO
        FUNFACTS_IMG_URL
        FUNFACTS_DESCRIPTION
        PHONEME_VIDEO_URL
        RIDDLE
        RIDDLE_ANSWER
        MORPHEME
        MORPHEME_MEANING
        MORPHEME_POSITION
        MORPHEME_ALTS
        NAME_MEANING
        NAME_PERSON
        NAME_NATIONALITY
        NAME_FLAG
        log_ngram_counts
        InterventionSelectionMetadata {
          pk
          locale
          cvc
          ERSS_default_ranked
          fallback_default
          high_default
          high_default_ranked
          low_default
          low_default_ranked
          wer
        }
        GSF {
          categories {
            description
            id
          }
          words {
            category
            word
          }
        }
        SYLLABLE_ENCODING
        ALPHSYL
      }
    }`;

    // Note: cache will invalidate if wordsQuery changes. Cache will not invalidate
    // if backend changes

    const wordsData = {}

    const wordsToFetch = [];

    // Get words from cache, and set array to fetch those that aren't
    console.log("Started wordmetadata request ",Date.now());
    for (let i = 0; i < words.length; i++) {
      const inWord = words[i];
      const wordForQuery = inWordToQueryWord(inWord, wordLocale);
      const key = hash(wordsQuery + inWord + META_VERSION);

      wordsData[wordForQuery] = {
        wordForQuery,
        inWord,
        key
      };

      let rawMetadata = sessionStorage.getItem(key);
      if(!rawMetadata || hasTimedOut(rawMetadata, key)){
        wordsToFetch.push(wordForQuery);
      } else {
        wordsData[wordForQuery].metaData = JSON.parse(rawMetadata).content;
      }
    }
    
    // If there are words to fetch, fetch them.  Then, loop through and process each word that is not null.
    //   Null would mean there is no entry for that word
    if (wordsToFetch.length > 0) {
      const fetchedWordsMetadata = await graphQLRequestIAM(SRSurl, wordsQuery, { words: wordsToFetch });
      for (let i = 0; i < fetchedWordsMetadata.data.words.length; i++) {
        if (fetchedWordsMetadata.data.words[i]) {
          const wordMetadata = fetchedWordsMetadata.data.words[i];
          const queryWord = wordMetadata.WORD;
          const { inWord, key } = wordsData[queryWord];

          const processedMetadata = ProcessWordFromDB(wordMetadata, inWord);
          wordsData[queryWord].metaData = await StoreWord(processedMetadata, key);
        } 
      }
    }

    // Loop through the original words, and sort the return array into the same order as param: words
    //   If a word does not have metadata (nothing in cache, null respone) let ProcessAndStoreWord handle appropriately
    const metadataInOrder = [];
    for (let i = 0; i < words.length; i++) {
      const inWord = words[i];
      const queryWord = inWordToQueryWord(inWord, wordLocale);
      const key = wordsData[queryWord].key;

      if (!wordsData[queryWord].metaData) {
        const processedMetadata = ProcessWordFromDB(null, inWord);
        wordsData[queryWord].metaData = StoreWord(processedMetadata, key)
      }

      // Push a copy.  Reference was being stored, and mods to one entry of same word was modifying all
      metadataInOrder.push({...wordsData[queryWord].metaData});
    }

    // TODO consider doing some validation on wordMetadata
    return(metadataInOrder);
  }
  catch(err)
  {
    console.warn("Error in getWordMetadataList:", err?.message, "timestamp:", Date.now()); // uses await to resolve responses, so start timestamp closest to this log is accurate
    
    // Retry logic - retry up to 3 times
    if (retryCount < MAX_RETRIES) {
      console.warn(`Retrying getWordMetadataList (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      return getWordMetadataList(words, retryCount + 1);
    }
    
    console.error("words failed to fetch after retries:", JSON.stringify(words));
    logSentryException(new Error('Word metadata list fetch failure'), 'Error in getWordMetadataList', { originalError: err?.message, originalStack: err?.stack });
    logSessionInfo("getWordMetadataList_after_retries", {
      maxRetries: MAX_RETRIES,
      error: err?.message || err,
      words: words
    });
    return (words);
  }
}

// returns true if the content is more than an hour old
function hasTimedOut(rawMetadata, key) {
  let data = JSON.parse(rawMetadata);

  // calculate expiration time for content,
  // to force periodic refresh after 60 minutes
  let now = new Date();
  let expiration = new Date(data.timestamp);
  expiration.setMinutes(expiration.getMinutes() + 60);

  // ditch the content if too old
  if (now.getTime() > expiration.getTime()) {
      sessionStorage.removeItem(key);
      return true;
  }
  return false;
}

function ProcessWordFromDB(metadata, inWord) {
  let wordMetadata = { ...metadata };

  if(!wordMetadata) {
    //TODO: This wasn't being caught anywhere - figure out error handling: throw "undefined metadata";
    console.log("META:",inWord, "|UNDEFINED");
    return({WORD: inWord});
  }

  wordMetadata.WORD = inWord;
  wordMetadata.FUN_FACT = wordMetadata.FUNFACTS_DESCRIPTION ? true : false;
  if(wordMetadata.PHON){
    wordMetadata.abPHON = ipaToAmirabet(wordMetadata.PHON, getUserData().locale);
  }

  if((wordMetadata.VIDEO_URL) && process.env.ASSET_ROOT){
    wordMetadata.VIDEO_URL = wordMetadata.VIDEO_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordMetadata.PHONEME_VIDEO_URL) && process.env.ASSET_ROOT){
    wordMetadata.PHONEME_VIDEO_URL = wordMetadata.PHONEME_VIDEO_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordMetadata.IMG_URL) && process.env.ASSET_ROOT){
    wordMetadata.IMG_URL = wordMetadata.IMG_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordMetadata.FUNFACTS_IMG_URL) && process.env.ASSET_ROOT){
    wordMetadata.FUNFACTS_IMG_URL = wordMetadata.FUNFACTS_IMG_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }

  //null out the entire DEFINITION object if there is no default definition
  let def = wordMetadata.DEFINITION;
  if(def && !def.default){
    wordMetadata.DEFINITION = null;
  }

  //Fix improperly stored MORPHEME_ALTS
  let morpheme_alts = wordMetadata.MORPHEME_ALTS;
  if(morpheme_alts && morpheme_alts.length === 1 && morpheme_alts[0].indexOf(',') >= 0){
    const newAlts = morpheme_alts[0].split(',');
    wordMetadata.MORPHEME_ALTS = newAlts;
  }

  return wordMetadata;
}

// fetches a word's metadata from the API and stores it in sessionStorage
// with a timestamp
async function StoreWord(wordMetadata, key) {
  sessionStorage.setItem(key, JSON.stringify({
    timestamp: new Date(),
    content: wordMetadata,
  }));

  return wordMetadata;
}

async function fetchAndStoreWord(wordQuery, queryWord, inWord, key) {
  const wordData = await graphQLRequestIAM(SRSurl, wordQuery, { word: queryWord });
  let wordMetadata = wordData.data.word.items[0];
  if(!wordMetadata) {
    //TODO: This wasn't being caught anywhere - figure out error handling: throw "undefined metadata";
    console.log("META:",inWord, "|UNDEFINED");
    return({WORD: inWord});
  }

  wordMetadata.WORD = inWord;
  wordMetadata.FUN_FACT = wordData.data.word.items[0].FUNFACTS_DESCRIPTION ? true : false;
  if(wordMetadata.PHON){
    wordMetadata.abPHON = ipaToAmirabet(wordMetadata.PHON, getUserData().locale);
  }

  if((wordData.data.word.items[0].VIDEO_URL) && process.env.ASSET_ROOT){
    wordData.data.word.items[0].VIDEO_URL = wordData.data.word.items[0].VIDEO_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordData.data.word.items[0].PHONEME_VIDEO_URL) && process.env.ASSET_ROOT){
    wordData.data.word.items[0].PHONEME_VIDEO_URL = wordData.data.word.items[0].PHONEME_VIDEO_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordData.data.word.items[0].IMG_URL) && process.env.ASSET_ROOT){
    wordData.data.word.items[0].IMG_URL = wordData.data.word.items[0].IMG_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }
  if((wordData.data.word.items[0].FUNFACTS_IMG_URL) && process.env.ASSET_ROOT){
    wordData.data.word.items[0].FUNFACTS_IMG_URL = wordData.data.word.items[0].FUNFACTS_IMG_URL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
  }

  //null out the entire DEFINITION object if there is no default definition
  let def = wordData.data.word.items[0].DEFINITION;
  if(def && !def.default){
    wordData.data.word.items[0].DEFINITION = null;
  }

  //Fix improperly stored MORPHEME_ALTS
  let morpheme_alts = wordData.data.word.items[0].MORPHEME_ALTS;
  if(morpheme_alts && morpheme_alts.length === 1 && morpheme_alts[0].indexOf(',') >= 0){
    const newAlts = morpheme_alts[0].split(',');
    wordData.data.word.items[0].MORPHEME_ALTS = newAlts;
  }

  sessionStorage.setItem(key, JSON.stringify({
    timestamp: new Date(),
    content: wordMetadata,
  }));
  return wordMetadata;
}

// TODO
// 1. Add a wrapper function to abstract phraseIndex
// 2. Abstract into intervention selection
export async function prefetchWordMetadata(inPhrase, phraseIndex, metadataOverrides) {
  let splitPhrase = cleanAndSplit(inPhrase, true);

  return getWordMetadataList(splitPhrase)
      .then((list)=>{
        for (let i = 0; i < list.length; i++) {
          // Override metadata from embedded overrides in story
          if (metadataOverrides) {
            // Check phrase and word index, but also check word as a extra precaution for potential bad data.
            const wordOverride = metadataOverrides.find(o => o.phraseIndex === phraseIndex && o.wordIndex === i && o.metadata && o.metadata.WORD.toUpperCase() === list[i].WORD.toUpperCase());
            if (wordOverride) {
              Object.keys(wordOverride.metadata).forEach(key => {
                if (wordOverride.metadata[key]) {
                  list[i][key] = wordOverride.metadata[key];
                } else if (wordOverride.metadata[key] === "") {
                  list[i][key] = null;
                }
                list[i].IS_OVERRIDE = true;
              })
              list[i] = ProcessWordFromDB(list[i], list[i].WORD);;
            }
          }
          const meta = list[i]
          if(RUN_METADATA_DUMP) {
            if(meta){
              console.log("META:",meta.WORD,"|",meta.IMG_URL,"|", meta.VIDEO_URL,"|", meta.DEFINITION ? meta.DEFINITION.default : null, "|",meta.RHYME); //###Leave in for logging metadata
            }else{
              console.log("META: ",word,"|","NO WORD METADATA")
            }
          }
        }

        setPrefetchedMetadataAIFeature(addPhonLengthMatchesGraphemeLength(list), phraseIndex);
        return list;
      });
}

function addPhonLengthMatchesGraphemeLength(metadataArray = []) {
  let metadataArrayWithPhons = [];
  metadataArray.forEach(metadata => {
    let filenames = metadata && metadata.PHON && findFilenames(metadata.PHON);
    if(!metadata.GRAPHEMIC_BREAKDOWN || !metadata.PHON || !filenames || !metadata.GRAPHEMIC_BREAKDOWN.split("/") || !filenames.length) {
      metadata.PHON_GRAPHEME_MATCH = false;
      metadataArrayWithPhons.push(metadata);
      return;
    }
    let phonLength = filenames.length;
    let graphemeLength = metadata.GRAPHEMIC_BREAKDOWN.split("/").length;
    if(phonLength == graphemeLength) {
      metadata.PHON_GRAPHEME_MATCH = true;
    } else {
      metadata.PHON_GRAPHEME_MATCH = false;
    }
    metadataArrayWithPhons.push(metadata);
  });
  return metadataArrayWithPhons;
}

export async function getRandomDistractorImages(wordMetadata) {
  try {
    const s3BucketParams = {
      bucket: "amira-assets-2",
      level: "public",
      region: 'us-east-2',
      customPrefix: { public: "", },
    };
    const objects = await Storage.list('images/distractors/', s3BucketParams);

    //filter out s3 folder objects
    const distractorImages = objects.filter(obj => obj.key.match(/\.[0-9a-z]+$/i));

    const randomDistractorImageUrls = [];
    const NUM_DISTRACTORS = 2;
    while(randomDistractorImageUrls.length < NUM_DISTRACTORS){
      const randomImg = distractorImages[Math.floor(Math.random() * distractorImages.length)];
      const randomImgUrl = `https://${process.env.ASSET_ROOT}/${randomImg.key}`;
      if(!randomImgUrl.includes(wordMetadata.WORD.toLowerCase()) && !randomDistractorImageUrls.includes(randomImgUrl)){
        randomDistractorImageUrls.push(randomImgUrl);
      }
    }
    return randomDistractorImageUrls;
  } catch (error) {
    console.error("Error in getRandomDistractorImages:", error);
    logSessionInfo("error_in_getRandomDistractorImages", {
      error: error,
      wordMetadata: wordMetadata,
    });
    return [];
  }
}

// returns true if the PHON is valid
export function isValidPHON(phon) {
  // if PHON is blank or null, invalid
  if(!phon || phon == '' || phon == ' ') {
    return false;
  }
  // if PHON includes invalid chars, invalid
  if (phon.includes("@") || phon.includes("&")) {
    return false;
  }
  return true;
}

const subSequence = ['b', 'c', 'd', 'f', 'g', 'h', 'l', 'm', 'n', 'p', 'r', 's', 't', 'w', 'k', 'j', 'y', 'z']

export async function getSubMetadata(wordMetadata, mostRecentScores) {

  if (!wordMetadata || !mostRecentScores || !wordMetadata.GRAPHEMIC_BREAKDOWN) return {subData: [], position: 'start'}
  const {wcpm=0} = mostRecentScores;
  const {GRAPHEMIC_BREAKDOWN} = wordMetadata
  const graphemes = GRAPHEMIC_BREAKDOWN.split('/')

    // determine SUB_POSITION
    let position = 'start';

    let threshold = 89;
    if (wcpm >= 15) threshold = 73;
    if (wcpm >= 25) threshold = 45;

    // generate rand int [0, 99]
    const i = Math.floor(Math.random() * 100);
    if (i > threshold) position = 'end';

  const inWord = graphemes.join('')
  const changeIndex = position === 'start' ? 0 : graphemes.length-1;

  const requestWords = [];
  let rhymeMetadata;

  for (const letter of subSequence) {
    graphemes[changeIndex] = letter;
    const word = graphemes.join('')
    // ignore inWord
    if (word === inWord) continue;
    requestWords.push(word)
  }

  if(wordMetadata && mostRecentScores && wordMetadata.RHYME) {
    const {RHYME} = wordMetadata;

    const list = await getWordMetadataList([RHYME]);
    const rhymeMetadata = list[0];
    if(rhymeMetadata) {
      const {GRAPHEMIC_BREAKDOWN, PHON} = rhymeMetadata;
      if(GRAPHEMIC_BREAKDOWN) rhymeMetadata.RHYME_GRAPHEMIC_BREAKDOWN = GRAPHEMIC_BREAKDOWN;
      if(PHON) rhymeMetadata.RHYME_PHON = PHON;
    }
  }

  const subData = await getWordMetadataList(requestWords);

  if(rhymeMetadata) {
    const checkForRhyme = subData.findIndex(wordData => wordData.WORD === rhymeMetadata.WORD);
    if(checkForRhyme > -1) {
      subData.unshift(rhymeMetadata);
      subData.splice(checkForRhyme + 1, 1);
    }
  }

  // Filter out content-sensitive candidates whose SENSITIVITY_TAGS overlap
  // with the current user's license sensitivityTags (e.g. ["ME"] for Middle East).
  // Words without tags or users without tags pass through unfiltered.
  // Tags are compared case-insensitively ("me" on a license matches "ME" on a word).
  const userSensitivityTags = (getUserData().sensitivityTags || []).filter(Boolean).map(tag => tag.toUpperCase());
  const filteredSubData = userSensitivityTags.length
    ? subData.filter(subDatum => {
        const wordTags = subDatum.SENSITIVITY_TAGS;
        if (wordTags?.length && wordTags.some(tag => tag && userSensitivityTags.includes(tag.toUpperCase()))) {
          console.log(`[getSubMetadata] Blocked content-sensitive word: ${subDatum.WORD}`);
          return false;
        }
        return true;
      })
    : subData;

  return {subData: filteredSubData, position}
}
